<template>
  <div class="chart-w dynamic-chart">
    <div class="dynamic-chart__wrapper">
      <Apexchart
        :key="`${selectedChart}-${refreshCounter}`"
        :options="chartOptions"
        :series="series?.[0]?.named ? toRaw(series) : []"
        :height="stylesChart.heightAreaChart"
      />
    </div>

    <div class="dynamic-chart__menu">
      <div class="dynamic-chart__menu-left">
        <div class="dynamic-chart__title">
          {{ t('news_dynamic_chart.title') }}
        </div>
      </div>

      <div
        id="chart-control"
        class="dynamic-chart__menu-right"
      >
        <RefreshIcon @click="loadData" />
        <CalendarInput v-model="date" />
        <RubricList
          v-model:selected-chart="selectedChart"
          :active-store="activeStore"
          :date="date"
          all-enabled
          send-param-name
          stop-load-on-clear
          @clearRubrics="clearRubrics"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, toRaw } from "vue";
import { useNewsStatDynamicBlocksStore } from "@/store/Analytic/chart/news-stat-dynamic-blocks";
import { useNewsStatDynamicRubricStore } from "@/store/Analytic/chart/news-stat-dynamic-rubric";
import { useRubricsStore } from "@/store/NewsFeed/rubrics";
import { useBlockStore } from "@/store/NewsFeed/blocks";
import VueApexCharts from "vue3-apexcharts";
import { stylesChart } from "@/tools/tooltipChart";
import RubricList from "@/components/AnalyticsPage/AnalyticsGeneral/Charts/blocks/RubricList.vue";
import useAreaChart from "@/mixins/apexChart/areaChart";
import useFilterDate from "@/mixins/apexChart/filterDate";
import { t } from "@/controllerLK/GlobusLocalization";
import CalendarInput from "@/components/AnalyticsPage/AnalyticsGeneral/Charts/blocks/CalendarInput.vue";
import RefreshIcon from "@/components/Icons/RefreshIcon.vue";

export default {
  name: "NewsDynamicChart",
  components: {CalendarInput, Apexchart: VueApexCharts, RubricList, RefreshIcon },
  setup() {
    const { date } = useFilterDate(loadData);
    const selectedChart = ref(0);
    const refreshCounter = ref(0);

    const statStorage = useNewsStatDynamicBlocksStore();
    const statRubricStorage = useNewsStatDynamicRubricStore();
    const rubricsStore = useRubricsStore();
    const blockStore = useBlockStore();

    const activeStore = computed(() => {
      return selectedChart.value === 0 ? statStorage : statRubricStorage;
    });

    const series = computed(() => {
      const kind = selectedChart.value === 0 ? blockStore.allThemes : rubricsStore.allRubrics;
      return (activeStore.value.items || [])
        .filter((item) => {
          // Фильтруем элементы, для которых найдено соответствующее имя темы/рубрики (задача 1772)
          const foundName = kind.find((theme) => theme.id == item.name);
          return foundName && foundName.name;
        })
        .map((item) => {
          const name = kind.find((theme) => theme.id == item.name)?.name;
          return {
            name: name,
            data: item.data.map((point) => point.count),
            named: name // Для проверки, что имя из стора, а не item.name (ID).
          };
        });
    });

    const categories = computed(() => {
      const labelsSet = new Set();
      (activeStore.value.items || []).forEach((item) => {
        item.data.forEach((point) => {
          labelsSet.add(point.date);
        });
      });
      return Array.from(labelsSet).sort((a, b) => new Date(a) - new Date(b));
    });

    const changeChart = (chart) => {
      if (selectedChart.value === chart) {
        return (selectedChart.value = 0);
      }
      selectedChart.value = chart;
    };
    
    const { chartOptions } = useAreaChart(
      {
        titleText: t("news_dynamic_chart.title"),
      },
      categories,
      series,
    );

    async function loadData() {
      await statStorage.loadStat(date.value[0], date.value[1]);
      await statRubricStorage.loadStat(date.value[0], date.value[1]);
    }

    const clearRubrics = () => {
      statStorage.items = [];
      statRubricStorage.items = [];
      refreshCounter.value++;
      chartOptions.value = {
        ...chartOptions.value,
        noData: { text: t("common_chart.no_data.short_text") },
      };
    };

    return { chartOptions, series, stylesChart, date, categories, loadData, selectedChart, changeChart, activeStore, refreshCounter, clearRubrics, toRaw, t };
  },
};
</script>

<style lang="scss" scoped>
.dynamic-chart {
  &__title {
    font-size: 16px;
    font-weight: 700;
    line-height: 22px;
  }

  &__menu {
    display: flex;
    position: absolute;
    align-items: center;
    top: 24px;
    height: 42px;
    width: 100%;
    left: 0;
    padding: 0 24px;
    justify-content: space-between;

    &-left {
      display: flex;
      gap: 20px;
    }

    &-right {
      display: flex;
      gap: 14px;
    }
  }
}

.apexcharts-menu-open {
  margin-top: 15px;
}
</style>
